import { FunctionComponent, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add } from '@mui/icons-material';
import { TransactionFromLocationField } from '../form-fields/TransactionFromLocationField';
import { TransactionToLocationField } from '../form-fields/TransactionToLocationField';
import { TransactionDateField } from '../form-fields/TransactionDateField';
import { TransactionStatusField } from '../form-fields/TransactionStatusField';
import { TransactionCarrierField } from '../form-fields/TransactionCarrierField';
import { TransactionDocumentNumberField } from '../form-fields/TransactionDocumentNumberField';
import { TransactionReferenceNumberField } from '../form-fields/TransactionReferenceNumberField';
import { TransactionNotesField } from '../form-fields/TransactionNotesField';
import { TransactionAssetAmountField } from '../form-fields/TransactionAssetAmountField';
import { transactionService } from '../../api/transactions/transaction.service';
import { TransactionType, TransactionStatus } from '../../api/transactions/transaction.contracts';
import { CreateTransactionModalFormModel } from '../CreateTransactionModal/CreateTransactionModalFormModel';
import { useTranslation } from '@/lib';
import { AccordionSection } from '@/components/accordion';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { TransactionProvider, useTransactionContext } from '../../context/TransactionContext';
import { ConfirmationDialog } from '@/components/modals/ConfirmationDialog';
import { CopyToClipboardButton } from '@/components/inputs/CopyToClipboardButton';

interface EditTransactionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  transactionId: number | null;
  isGuestMode?: boolean;
}

const EditTransactionModalContent: FunctionComponent<EditTransactionModalProps> = ({ open, onClose, onSuccess, transactionId, isGuestMode }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [transactionType, setTransactionType] = useState<TransactionType>(TransactionType.Transfer);
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatus>(TransactionStatus.InProgress);
  const [transactionIdString, setTransactionIdString] = useState<string>('');
  const { form, fieldArray, hasAvailableAssetTypes } = useTransactionContext();

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  const { fields, append, remove } = fieldArray;

  useEffect(() => {
    if (open && transactionId) {
      fetchTransaction();
    }
  }, [open, transactionId]);

  const fetchTransaction = async () => {
    if (!transactionId) return;

    setLoading(true);
    try {
      const response = await transactionService.getById(transactionId);

      if (response.isSuccess && response.payload) {
        const transaction = response.payload;

        // Set form values
        setValue('fromLocationId', transaction.fromLocationId);
        setValue('toLocationId', transaction.toLocationId);
        setValue('date', transaction.date ? dayjs(transaction.date) : dayjs());
        setValue('carrierId', transaction.carrierId);
        setValue('documentNumber', transaction.documentNumber || '');
        setValue('referenceNumber', transaction.referenceNumber || '');
        setValue('notes', transaction.notes || '');

        // Set assets from details
        const assets =
          transaction.details?.map((detail) => ({
            assetTypeId: detail.assetType.id,
            amount: detail.amount,
          })) || [];
        setValue('assets', assets);

        // Set transaction type, status, and ID
        setTransactionType(transaction.type);
        setTransactionStatus(transaction.status);
        setTransactionIdString(transaction.transactionId);
      } else {
        toast.error(t('error_loading_transaction'));
      }
    } catch (error) {
      console.error('Error fetching transaction:', error);
      toast.error(t('error_loading_transaction'));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: CreateTransactionModalFormModel) => {
    if (!transactionId) return;

    // Prevent updates to finalized transactions
    if (transactionStatus === TransactionStatus.Finalized) {
      toast.error(t('cannot_update_finalized_transaction'));
      return;
    }

    // Validation ensures these are defined, but TypeScript doesn't know that
    if (!data.fromLocationId || !data.toLocationId) {
      console.error('Location IDs are required');
      return;
    }

    try {
      // Filter out assets that don't have an assetTypeId selected and map to UpdateTransactionDetailDTO
      const details =
        data.assets
          ?.filter((asset) => asset.assetTypeId && asset.assetTypeId > 0)
          .map((asset) => ({
            assetTypeId: asset.assetTypeId,
            amount: asset.amount,
          })) || [];

      const response = await transactionService.update(transactionId, {
        type: transactionType,
        status: transactionStatus,
        toLocationId: data.toLocationId,
        fromLocationId: data.fromLocationId,
        transactionDate: data.date.toISOString(),
        carrierId: data.carrierId,
        documentNumber: data.documentNumber || '',
        referenceNumber: data.referenceNumber || '',
        notes: data.notes || '',
        details: details,
      });

      if (!response.isSuccess) {
        toast.error(t('error_updating_transaction'));
        return;
      }

      toast.success(t('transaction_updated'));
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error(t('error_updating_transaction'));
    }
  };

  const handleCloseAttempt = () => {
    // Check if form has unsaved changes
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  // Get the transaction type name for the title
  const getTransactionTypeName = () => {
    switch (transactionType) {
      case TransactionType.Transfer:
        return t('transfer');
      case TransactionType.Correction:
        return t('correction');
      default:
        return t('transaction');
    }
  };

  const isFinalized = transactionStatus === TransactionStatus.Finalized;

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="lg" fullWidth PaperProps={{ className: 'bg-gray-50 dark:bg-gray-900' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle className="px-6 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <span>
                {t('edit')} {getTransactionTypeName()}
                {transactionIdString && (
                  <span className="text-gray-600 dark:text-gray-400">
                    {' '}
                    ({transactionIdString}
                    {
                      <CopyToClipboardButton
                        copyContent={transactionIdString}
                        tooltipText={t('copy_transaction_id')}
                        size="small"
                        iconSize="inherit"
                      />
                    }
                    )
                  </span>
                )}
              </span>
            </div>
          </DialogTitle>
          <DialogContent className="px-6 pt-2">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <CircularProgress />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <TransactionFromLocationField control={control} errors={errors} autoFocus disabled={isFinalized} />
                  <TransactionToLocationField control={control} errors={errors} disabled={isFinalized} />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <TransactionDateField control={control} errors={errors} isEditMode disabled={isFinalized} />
                  <TransactionStatusField value={transactionStatus} onChange={setTransactionStatus} disabled={isSubmitting || isFinalized} />
                </div>

                <AccordionSection title={t('additional_details')} defaultExpanded={true}>
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <TransactionDocumentNumberField control={control} errors={errors} disabled={isFinalized} />
                      <TransactionReferenceNumberField control={control} errors={errors} disabled={isFinalized} />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <TransactionCarrierField control={control} errors={errors} disabled={isFinalized} />
                      <TransactionNotesField control={control} errors={errors} disabled={isFinalized} />
                    </div>
                  </div>
                </AccordionSection>

                <AccordionSection title={t('assets')} defaultExpanded={true}>
                  <div className="flex flex-col">
                    {fields.map((field, index) => (
                      <TransactionAssetAmountField
                        key={field.id}
                        control={control}
                        errors={errors}
                        index={index}
                        onRemove={() => remove(index)}
                        canRemove={fields.length > 1}
                        disabled={isFinalized}
                      />
                    ))}

                    <div className="mt-2">
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => append({ assetTypeId: undefined, amount: 1 })}
                        size="small"
                        disabled={!hasAvailableAssetTypes || isFinalized}
                      >
                        {t('add_asset')}
                      </Button>
                    </div>
                  </div>
                </AccordionSection>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAttempt} disabled={isSubmitting} variant="outlined">
              {t('cancel')}
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting} disabled={isFinalized}>
              {t('save')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('unsaved_changes')}
        message={t('unsaved_changes_message')}
        confirmText={t('discard_changes')}
      />
    </>
  );
};

export const EditTransactionModal: FunctionComponent<EditTransactionModalProps> = (props) => {
  return (
    <TransactionProvider open={props.open} isGuestMode={props.isGuestMode}>
      <EditTransactionModalContent {...props} />
    </TransactionProvider>
  );
};
